import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { api } from './api';
import { randomToken } from './image';
import type { BookingConfirmation, PublicData, QueueProgress, Stencil } from './types';

type Step = 'welcome' | 'gallery' | 'form' | 'confirmation' | 'queue';

function Turnstile({ onToken }: { onToken: (token: string) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const sitekey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
    if (!sitekey || sitekey.includes('replace_me')) return;
    let widgetId: string | undefined;
    const render = () => {
      if (ref.current && window.turnstile && !widgetId) {
        widgetId = window.turnstile.render(ref.current, {
          sitekey, action: 'booking', theme: 'dark',
          callback: (token: string) => onToken(token),
          'expired-callback': () => onToken(''), 'error-callback': () => onToken('')
        });
      }
    };
    const existing = document.querySelector<HTMLScriptElement>('script[data-turnstile]');
    if (existing) { existing.addEventListener('load', render); render(); }
    else {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true; script.defer = true; script.dataset.turnstile = 'true'; script.addEventListener('load', render);
      document.head.appendChild(script);
    }
    return () => { if (widgetId) window.turnstile?.remove(widgetId); existing?.removeEventListener('load', render); };
  }, [onToken]);
  return <div className="turnstile-wrap"><div ref={ref} />{!import.meta.env.VITE_TURNSTILE_SITE_KEY && <small>Turnstile non configurato</small>}</div>;
}

export default function GuestApp() {
  const [data, setData] = useState<PublicData | null>(null);
  const [step, setStep] = useState<Step>('welcome');
  const [selected, setSelected] = useState<Stencil | null>(null);
  const [confirmation, setConfirmation] = useState<BookingConfirmation | null>(null);
  const [queueProgress, setQueueProgress] = useState<QueueProgress | null>(null);
  const [queueError, setQueueError] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [form, setForm] = useState({ first_name: '', last_name: '', phone: '', has_allergies: false,
    allergies_details: '', takes_medication: false, medication_details: '', privacy_accepted: false });
  const credentials = useRef({ idempotencyKey: crypto.randomUUID(), receiptToken: randomToken() });

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const next = await api<PublicData>('/api/public');
      setData(next);
      const saved = JSON.parse(localStorage.getItem('weddink_receipt') || 'null') as { event_id?: string; booking_id?: string; receipt_token?: string } | null;
      if (next.event && saved?.event_id === next.event.id && saved.booking_id && saved.receipt_token) {
        const receipt = await api<BookingConfirmation>(`/api/receipt/${encodeURIComponent(saved.booking_id)}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ receipt_token: saved.receipt_token })
        });
        setConfirmation(receipt); setStep('confirmation');
      }
    } catch (e) { setError(e instanceof Error ? e.message : 'Impossibile caricare WeddInk'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (step !== 'queue') return;
    let active = true;
    const refreshQueue = async () => {
      try {
        const next = await api<QueueProgress>('/api/queue');
        if (active) { setQueueProgress(next); setQueueError(''); }
      } catch (e) {
        if (active) setQueueError(e instanceof Error ? e.message : 'Avanzamento non disponibile');
      }
    };
    void refreshQueue();
    const timer = window.setInterval(() => void refreshQueue(), 4000);
    return () => { active = false; window.clearInterval(timer); };
  }, [step]);

  const heroStyle = useMemo(() => {
    const event = data?.event;
    const background = event?.background_mode === 'image' && event.background_image_url
      ? `linear-gradient(rgba(0,0,0,${event.background_overlay}),rgba(0,0,0,${event.background_overlay})),url(${event.background_image_url})`
      : event?.background_color || '#111112';
    return { background, backgroundSize: 'cover', backgroundPosition: event?.background_position || 'center' };
  }, [data]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!data?.event || !selected) return;
    if (!turnstileToken) { setError('Completa la verifica antibot'); return; }
    setSubmitting(true); setError('');
    try {
      const result = await api<BookingConfirmation>('/api/bookings', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, event_id: data.event.id, public_token: data.event.public_token,
          stencil_id: selected.id, idempotency_key: credentials.current.idempotencyKey,
          receipt_token: credentials.current.receiptToken, turnstile_token: turnstileToken })
      });
      localStorage.setItem('weddink_receipt', JSON.stringify({ event_id: data.event.id, booking_id: result.booking_id, receipt_token: credentials.current.receiptToken }));
      setConfirmation(result); setStep('confirmation');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Invio non riuscito');
      setTurnstileToken(''); window.turnstile?.reset();
    } finally { setSubmitting(false); }
  }

  if (loading) return <main className="center-state"><img className="app-logo compact" src="/weddink-logo.png" alt="WeddInk" /><p>Prepariamo l’esperienza…</p></main>;
  if (error && !data) return <main className="center-state"><img className="app-logo compact" src="/weddink-logo.png" alt="WeddInk" /><h1>WeddInk</h1><p>{error}</p><button onClick={() => void load()}>Riprova</button></main>;
  if (!data?.event) return <main className="center-state"><img className="app-logo compact" src="/weddink-logo.png" alt="WeddInk" /><h1>WeddInk</h1><p>Nessun matrimonio è attivo in questo momento.</p></main>;

  if (step === 'welcome') return (
    <main className={`guest-hero theme-${data.event.text_theme}`} style={heroStyle}>
      <div className="hero-content">
        {data.event.hero_image_url ? <img className="wedding-logo" src={data.event.hero_image_url} alt={data.event.hero_image_alt} /> : <img className="app-logo hero-logo" src="/weddink-logo.png" alt="WeddInk — anelli e ago da tatuaggio" />}
        <p className="eyebrow">Un ricordo inciso nel tempo</p>
        <h1>{data.event.welcome_title}</h1>
        <div className="signature">{data.event.welcome_signature}</div>
        {data.event.welcome_message && <p className="welcome-message">{data.event.welcome_message}</p>}
        <button className="primary gold" onClick={() => setStep('gallery')}>Scegli il tuo tatuaggio</button>
      </div>
    </main>
  );

  if (step === 'gallery') return (
    <main className="guest-page">
      <header className="guest-header"><button className="text-button" onClick={() => setStep('welcome')}>← Indietro</button><span>WeddInk</span></header>
      <section className="content-shell">
        <p className="eyebrow">{data.event.couple_name}</p><h1>Scegli il tuo segno</h1>
        <p className="lead">Un solo stencil, scelto da te. Potrai ancora cambiarlo prima della conferma.</p>
        {data.stencils.length ? <div className="stencil-grid">{data.stencils.map((stencil) => (
          <button key={stencil.id} className={`stencil-card ${selected?.id === stencil.id ? 'selected' : ''}`} onClick={() => setSelected(stencil)}>
            <img src={stencil.image_url} alt={stencil.name} /><span>{stencil.name}</span><i>{selected?.id === stencil.id ? 'Selezionato' : 'Scegli'}</i>
          </button>
        ))}</div> : <div className="empty-state">Gli stencil saranno disponibili a breve.</div>}
        <div className="sticky-action"><button className="primary" disabled={!selected} onClick={() => setStep('form')}>Continua{selected ? ` con “${selected.name}”` : ''}</button></div>
      </section>
    </main>
  );

  if (step === 'form' && selected) return (
    <main className="guest-page"><header className="guest-header"><button className="text-button" onClick={() => setStep('gallery')}>← Cambia stencil</button><span>WeddInk</span></header>
      <section className="content-shell narrow">
        <div className="selection-summary"><img src={selected.image_url} alt="" /><div><small>La tua scelta</small><strong>{selected.name}</strong></div></div>
        <h1>Completa la prenotazione</h1><p className="lead">I dati servono esclusivamente allo staff durante l’evento.</p>
        <form onSubmit={submit} className="booking-form">
          <label>Nome<input required maxLength={80} autoComplete="given-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></label>
          <label>Cognome<input required maxLength={80} autoComplete="family-name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></label>
          <label>Numero di telefono<input required maxLength={30} inputMode="tel" autoComplete="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></label>
          <fieldset><legend>Hai allergie?</legend><div className="choice-row"><button type="button" className={!form.has_allergies ? 'active' : ''} onClick={() => setForm({ ...form, has_allergies: false, allergies_details: '' })}>No</button><button type="button" className={form.has_allergies ? 'active' : ''} onClick={() => setForm({ ...form, has_allergies: true })}>Sì</button></div></fieldset>
          {form.has_allergies && <label>Specifica le allergie<textarea required maxLength={500} value={form.allergies_details} onChange={(e) => setForm({ ...form, allergies_details: e.target.value })} /></label>}
          <fieldset><legend>Assumi farmaci?</legend><div className="choice-row"><button type="button" className={!form.takes_medication ? 'active' : ''} onClick={() => setForm({ ...form, takes_medication: false, medication_details: '' })}>No</button><button type="button" className={form.takes_medication ? 'active' : ''} onClick={() => setForm({ ...form, takes_medication: true })}>Sì</button></div></fieldset>
          {form.takes_medication && <label>Indica quali farmaci<textarea required maxLength={500} value={form.medication_details} onChange={(e) => setForm({ ...form, medication_details: e.target.value })} /></label>}
          <label className="check-line"><input type="checkbox" required checked={form.privacy_accepted} onChange={(e) => setForm({ ...form, privacy_accepted: e.target.checked })} /><span>Ho letto l’informativa privacy fornita dagli organizzatori e accetto il trattamento dei dati per la gestione della prenotazione.</span></label>
          <Turnstile onToken={setTurnstileToken} />
          {error && <div className="error-banner">{error}</div>}
          <button className="primary" disabled={submitting}>{submitting ? 'Conferma in corso…' : 'Conferma prenotazione'}</button>
        </form>
      </section>
    </main>
  );

  if (step === 'queue' && confirmation) {
    const current = queueProgress?.event_id === data.event.id ? queueProgress.current_number : 0;
    return <main className="confirmation-page"><div className="confirmation-card queue-card">
      <p className="eyebrow">Avanzamento in tempo reale</p>
      {current > 0 ? <><p className="queue-label">Siamo arrivati al</p><div className="current-queue">#{String(current).padStart(3, '0')}</div></> : <><div className="current-queue">—</div><p>Le chiamate non sono ancora iniziate.</p></>}
      <div className="your-number"><span>Il tuo numero</span><strong>#{String(confirmation.queue_number).padStart(3, '0')}</strong></div>
      {queueProgress?.next_waiting_number != null && <small>Prossimo numero in attesa: #{String(queueProgress.next_waiting_number).padStart(3, '0')}</small>}
      {queueError && <div className="error-banner">{queueError}</div>}
      <p className="queue-refresh">Aggiornamento automatico ogni 4 secondi</p>
      <button className="secondary" onClick={() => setStep('confirmation')}>← Torna alla prenotazione</button>
    </div></main>;
  }

  return <main className="confirmation-page"><div className="confirmation-card"><div className="success-ring">✓</div><p className="eyebrow">Prenotazione confermata</p><h1>Numero <strong>#{String(confirmation?.queue_number || 0).padStart(3, '0')}</strong></h1>
    {confirmation && <div className="receipt-stencil"><img src={confirmation.stencil_image_url} alt="" /><span>{confirmation.stencil_name}</span></div>}
    <p>Questo numero determina l’ordine di chiamata. Conserva questa schermata: resterà disponibile su questo dispositivo.</p><small>Stato: {confirmation?.status === 'waiting' ? 'In attesa' : confirmation?.status}</small>
    <button className="primary queue-button" onClick={() => setStep('queue')}>Vedi a che numero siamo arrivati</button></div></main>;
}
